import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  BookOpen,
  RefreshCw,
  History,
  Trash2,
  Users,
  Keyboard,
  Package,
  Warehouse,
  Factory,
  List,
  Receipt,
  Database
} from 'lucide-react';

const menuItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, key: '1', adminOnly: false },
  { path: '/income', label: 'Income', icon: TrendingUp, key: '2', adminOnly: false },
  { path: '/expense', label: 'Expense', icon: TrendingDown, key: '3', adminOnly: false },
  { path: '/sales', label: 'Sales', icon: ShoppingCart, key: '4', adminOnly: false },
  { path: '/purchase', label: 'Purchase', icon: Package, key: '5', adminOnly: false },
  { path: '/inventory', label: 'Inventory', icon: Warehouse, key: '6', adminOnly: false },
  { path: '/production', label: 'Production', icon: Factory, key: '7', adminOnly: false },
  { path: '/ledger', label: 'Ledger', icon: BookOpen, key: '8', adminOnly: false },
  { path: '/recurring', label: 'Recurring', icon: RefreshCw, key: null, adminOnly: false },
  { path: '/master', label: 'Master', icon: Database, key: '9', adminOnly: false },
  { path: '/gst', label: 'GST', icon: Receipt, key: null, adminOnly: false },
  { path: '/all-entries', label: 'All Entries', icon: List, key: null, adminOnly: false },
  { path: '/audit-trail', label: 'Audit Trail', icon: History, key: null, adminOnly: true },
  { path: '/deleted-records', label: 'Deleted', icon: Trash2, key: '0', adminOnly: true },
  { path: '/users', label: 'Users', icon: Users, key: null, adminOnly: true },
];

export default function Homepage() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  const filtered = menuItems.filter(item => !item.adminOnly || isAdmin);

  return (
    <div className="h-[calc(100dvh-3.5rem)] max-h-[calc(100dvh-3.5rem)] overflow-hidden flex flex-col items-center justify-center px-4" data-testid="homepage">
      {/* Title */}
      <div className="text-center mb-[2.5vh] shrink-0">
        <h1 className="text-[clamp(1.1rem,2.5vw,1.875rem)] font-bold tracking-tight mb-0.5">
          Welcome, {user?.name}
        </h1>
        <p className="text-muted-foreground text-[clamp(0.65rem,1.2vw,0.875rem)]">Select a module to get started</p>
      </div>

      {/* Cards grid — perfectly centered */}
      <div
        className="grid grid-cols-3 sm:grid-cols-5 place-items-center mx-auto min-h-0 shrink"
        style={{
          width: 'min(750px, 92vw)',
          gap: 'clamp(8px, 1.5vw, 20px)',
        }}
      >
        {filtered.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className="group flex flex-col items-center justify-center rounded-2xl w-full
                bg-card border border-border/50
                shadow-[0_2px_8px_rgba(0,0,0,0.04)]
                hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)]
                hover:border-border
                hover:-translate-y-0.5
                active:translate-y-0 active:shadow-[0_2px_8px_rgba(0,0,0,0.04)]
                transition-all duration-200 ease-out cursor-pointer"
              style={{ gap: 'clamp(3px, 1vh, 10px)', padding: 'clamp(8px, 2vh, 24px) clamp(4px, 1vw, 16px)' }}
              data-testid={`home-card-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div
                className="rounded-xl bg-secondary/80 flex items-center justify-center
                  group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200"
                style={{
                  width: 'clamp(36px, 7vh, 64px)',
                  height: 'clamp(36px, 7vh, 64px)',
                }}
              >
                <Icon style={{ width: 'clamp(20px, 3.8vh, 34px)', height: 'clamp(20px, 3.8vh, 34px)' }} strokeWidth={1.5} />
              </div>
              <span className="font-medium text-foreground/80 group-hover:text-foreground transition-colors"
                style={{ fontSize: 'clamp(0.6rem, 1.4vh, 0.875rem)' }}>
                {item.label}
              </span>
              <kbd className="text-muted-foreground/60 bg-muted rounded font-mono"
                style={{
                  fontSize: 'clamp(0.5rem, 0.9vh, 0.625rem)',
                  padding: '1px clamp(3px, 0.5vw, 6px)',
                }}>
                {item.key !== null ? item.key : '\u00A0'}
              </kbd>
            </button>
          );
        })}
      </div>

      {/* Footer hint */}
      <div className="flex items-center justify-center gap-2 text-muted-foreground/50 mt-[1.5vh] shrink-0"
        style={{ fontSize: 'clamp(0.55rem, 1vh, 0.75rem)' }}>
        <Keyboard style={{ width: 'clamp(10px, 1.5vh, 14px)', height: 'clamp(10px, 1.5vh, 14px)' }} />
        <span>Press <kbd className="bg-muted rounded font-mono" style={{ padding: '0 3px', fontSize: 'inherit' }}>?</kbd> for keyboard shortcuts</span>
      </div>
    </div>
  );
}
